import * as React from "react";
import type { AdChannel } from "./types";
import { logTrackingDebug } from "../debug/debugLog";

type Snaptr = (...args: unknown[]) => void;

declare global {
  interface Window {
    snaptr?: Snaptr;
  }
}

function snaptr(...args: unknown[]): void {
  if (typeof window === "undefined") {
    return;
  }
  const [command, name, params] = args;
  // The email stays out of the debug log.
  const { user_email, ...rest } = (params as Record<string, unknown>) ?? {};
  logTrackingDebug("snap", `${command} ${name}`, user_email ? { ...rest, user_email: "…" } : rest);
  if (!window.snaptr) {
    logTrackingDebug("snap", "snaptr missing: pixel script not on the page");
    return;
  }
  window.snaptr(...args);
}

/**
 * Snap pixel. The <head> snippet loads scevent.min.js, inits the pixel and
 * sends PAGE_VIEW once per full page load; route changes send VIEW_CONTENT
 * (same split as Meta's PageView / ViewContent).
 *
 * Every event carries `client_dedup_id` (our event ID). PURCHASE is sent only
 * by lovelica-triggers through the Conversions API, with event_id = order ID.
 */
export function createSnapChannel(pixelID: string): AdChannel {
  // Snap hashes `user_email` in the browser before sending it.
  let userEmail: string | undefined;

  const track = (event: string, params: Record<string, unknown>, eventId: string) => {
    snaptr("track", event, {
      ...params,
      client_dedup_id: eventId,
      ...(userEmail ? { user_email: userEmail } : {}),
    });
  };

  return {
    name: "snap",

    headComponents: () => [
      <script
        key="snap-pixel"
        dangerouslySetInnerHTML={{
          __html: `(function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function(){a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};a.queue=[];var s='script';var r=t.createElement(s);r.async=!0;r.src=n;var u=t.getElementsByTagName(s)[0];u.parentNode.insertBefore(r,u);})(window,document,'https://sc-static.net/scevent.min.js');snaptr('init',${JSON.stringify(pixelID)});snaptr('track','PAGE_VIEW');`,
        }}
      />,
    ],

    identify: ({ email }) => {
      // Sent with each later event, so the SIGN_UP fired right after has it.
      userEmail = email.trim().toLowerCase();
    },

    track: (e, eventId) => {
      switch (e.name) {
        case "page-view":
          // PAGE_VIEW itself is sent once per full load by the <head> snippet.
          track("VIEW_CONTENT", {}, eventId);
          return;
        case "lead-created":
          track("SIGN_UP", {}, eventId);
          return;
        case "plan-selected":
          track("ADD_CART", pick(e.properties), eventId);
          return;
        case "page_view_checkout":
          track("START_CHECKOUT", pick(e.properties), eventId);
          return;
        case "add-payment-info":
          track("ADD_BILLING", pick(e.properties), eventId);
          return;
      }
    },
  };
}

function pick(p: { value: number; currency: string; productID: string }) {
  return { price: p.value, currency: p.currency, item_ids: [p.productID] };
}
